import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Loader2, SendHorizonal, Save, Trash2, Image } from 'lucide-react';

const ethiopianCities = [
  'Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle',
  'Dire Dawa', 'Jimma', 'Gondar', 'Dessie', 'Bishoftu',
  'Shashamane', 'Arba Minch', 'Harar', 'Debre Markos', 'Nekemte',
];

const commercialSubTypes = [
  { value: 'office', label: 'Office Space' },
  { value: 'retail', label: 'Retail / Shop' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'showroom', label: 'Showroom' },
  { value: 'hotel', label: 'Hotel / Hospitality' },
  { value: 'mixed-use', label: 'Mixed-Use' },
];

export function AddCommercialProperty() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Check if agent has agency membership
  const { data: agentProfile, isLoading: checkingAgency } = useQuery({
    queryKey: ['agent-agency-check-commercial', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agent_profiles').select('agency_id').eq('user_id', user!.id).single();
      return data;
    },
    enabled: !!user && hasRole('agent'),
  });

  const isAgentWithoutAgency = hasRole('agent') && !hasRole('agency_admin') && !hasRole('superadmin') && !checkingAgency && !agentProfile?.agency_id;

  const [listingType, setListingType] = useState<'sale' | 'rent'>('sale');
  const [commercialSubType, setCommercialSubType] = useState('office');
  const [city, setCity] = useState('');
  const [subCity, setSubCity] = useState('');
  const [woreda, setWoreda] = useState('');
  const [address, setAddress] = useState('');

  const [titleEn, setTitleEn] = useState('');
  const [titleAm, setTitleAm] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descAm, setDescAm] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('ETB');
  const [areaSqm, setAreaSqm] = useState('');
  const [floor, setFloor] = useState('');
  const [totalFloors, setTotalFloors] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [hasParking, setHasParking] = useState(false);
  const [parkingSpaces, setParkingSpaces] = useState('');
  const [hasLoadingDock, setHasLoadingDock] = useState(false);

  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [images, setImages] = useState<{ url: string; is_primary: boolean }[]>([]);
  const [uploading, setUploading] = useState(false);

  useQuery({
    queryKey: ['my-profile-for-commercial'],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase.from('profiles').select('*').eq('user_id', user.id).single();
      if (data) {
        if (!contactPerson && data.full_name) setContactPerson(data.full_name);
        if (!contactPhone && data.phone) setContactPhone(data.phone);
        if (!contactEmail && user.email) setContactEmail(user.email);
      }
      return data;
    },
    enabled: !!user,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !user) return;
    setUploading(true);
    try {
      const newImages: { url: string; is_primary: boolean }[] = [];
      for (const file of Array.from(e.target.files)) {
        const ext = file.name.split('.').pop();
        const path = `${user.id}/commercial/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from('property-images').upload(path, file);
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('property-images').getPublicUrl(path);
        newImages.push({ url: urlData.publicUrl, is_primary: images.length === 0 && newImages.length === 0 });
      }
      setImages(prev => [...prev, ...newImages]);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx: number) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      if (updated.length > 0 && !updated.some(img => img.is_primary)) updated[0].is_primary = true;
      return updated;
    });
  };

  const setPrimaryImage = (idx: number) => {
    setImages(prev => prev.map((img, i) => ({ ...img, is_primary: i === idx })));
  };

  const saveMutation = useMutation({
    mutationFn: async (submitForReview: boolean) => {
      if (!user) throw new Error('Not authenticated');
      if (!titleEn || !city || !price) throw new Error('Please fill all required fields (Title, City, Price)');

      const slug = titleEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);

      const { data: property, error } = await supabase
        .from('properties')
        .insert({
          user_id: user.id,
          title: { en: titleEn, am: titleAm || titleEn, om: '' } as any,
          description: { en: descEn, am: descAm || descEn, om: '' } as any,
          property_type: 'commercial' as any,
          listing_type: listingType as any,
          price: parseFloat(price),
          currency: currency as any,
          city,
          sub_city: subCity || null,
          woreda: woreda || null,
          address: address || null,
          area_sqm: areaSqm ? parseFloat(areaSqm) : null,
          floor: floor ? parseInt(floor) : null,
          total_floors: totalFloors ? parseInt(totalFloors) : null,
          year_built: yearBuilt ? parseInt(yearBuilt) : null,
          has_parking: hasParking,
          features: {
            commercial_sub_type: commercialSubType,
            parking_spaces: parkingSpaces ? parseInt(parkingSpaces) : null,
            loading_dock: hasLoadingDock,
          } as any,
          contact_person: contactPerson || null,
          contact_phone: contactPhone || null,
          contact_email: contactEmail || null,
          slug,
          status: submitForReview ? 'active' : 'draft',
          approval_status: submitForReview ? 'pending_review' : 'draft',
        } as any)
        .select()
        .single();

      if (error) throw error;

      if (images.length > 0) {
        const imgRows = images.map((img, i) => ({
          property_id: property.id,
          url: img.url,
          is_primary: img.is_primary,
          sort_order: i,
        }));
        await supabase.from('property_images').insert(imgRows);
      }

      return property;
    },
    onSuccess: (_, submitForReview) => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      toast.success(submitForReview ? 'Commercial property submitted for review!' : 'Saved as draft!');
      navigate('/dashboard/listings');
    },
    onError: (err: any) => toast.error(err.message),
  });

  if (isAgentWithoutAgency) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <h2 className="font-display text-xl font-bold mb-2">Agency Membership Required</h2>
        <p className="text-muted-foreground mb-4">You must belong to an agency to post commercial properties. Contact your agency admin for an invite link.</p>
        <Button onClick={() => navigate('/agencies')}>Browse Agencies</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/listings')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="font-display text-xl font-bold">Add Commercial Property</h2>
      </div>

      {/* Type & Location */}
      <Card>
        <CardHeader><CardTitle className="text-base">Commercial Type & Location</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">Purpose *</Label>
            <div className="flex gap-2">
              <Button type="button" variant={listingType === 'sale' ? 'default' : 'outline'} onClick={() => setListingType('sale')} className="flex-1">For Sale</Button>
              <Button type="button" variant={listingType === 'rent' ? 'default' : 'outline'} onClick={() => setListingType('rent')} className="flex-1">For Rent</Button>
            </div>
          </div>
          <div>
            <Label>Commercial Sub-Type *</Label>
            <Select value={commercialSubType} onValueChange={setCommercialSubType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {commercialSubTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>City *</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>{ethiopianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sub City</Label><Input value={subCity} onChange={e => setSubCity(e.target.value)} placeholder="e.g. Bole" /></div>
            <div><Label>Woreda</Label><Input value={woreda} onChange={e => setWoreda(e.target.value)} placeholder="e.g. Woreda 03" /></div>
          </div>
          <div><Label>Address / Landmark</Label><Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Near Edna Mall, Bole Road" /></div>
        </CardContent>
      </Card>

      {/* Details */}
      <Card>
        <CardHeader><CardTitle className="text-base">Property Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Title (English) *</Label><Input value={titleEn} onChange={e => setTitleEn(e.target.value)} placeholder="e.g. Prime Office Space in Bole" /></div>
            <div><Label>Title (Amharic)</Label><Input value={titleAm} onChange={e => setTitleAm(e.target.value)} placeholder="አማርኛ ርዕስ" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Description (English)</Label><Textarea value={descEn} onChange={e => setDescEn(e.target.value)} rows={4} placeholder="Describe the commercial property..." /></div>
            <div><Label>Description (Amharic)</Label><Textarea value={descAm} onChange={e => setDescAm(e.target.value)} rows={4} placeholder="ስለ ንብረቱ ይግለጹ..." /></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>Price *</Label><Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0" /></div>
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ETB">ETB (Birr)</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Area (sqm)</Label><Input type="number" value={areaSqm} onChange={e => setAreaSqm(e.target.value)} placeholder="0" /></div>
            <div><Label>Year Built</Label><Input type="number" value={yearBuilt} onChange={e => setYearBuilt(e.target.value)} placeholder="2024" /></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>Floor</Label><Input type="number" value={floor} onChange={e => setFloor(e.target.value)} placeholder="0" /></div>
            <div><Label>Total Floors</Label><Input type="number" value={totalFloors} onChange={e => setTotalFloors(e.target.value)} placeholder="0" /></div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={hasParking} onCheckedChange={setHasParking} id="parking" />
              <Label htmlFor="parking">Has Parking</Label>
            </div>
            {hasParking && (
              <div><Label>Parking Spaces</Label><Input type="number" value={parkingSpaces} onChange={e => setParkingSpaces(e.target.value)} placeholder="0" /></div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={hasLoadingDock} onCheckedChange={setHasLoadingDock} id="loading-dock" />
            <Label htmlFor="loading-dock">Has Loading Dock</Label>
          </div>
        </CardContent>
      </Card>

      {/* Images */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4" />Photos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed rounded-xl p-8 justify-center hover:border-primary transition-colors">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            <div className="text-center">
              <span className="text-sm font-medium">{uploading ? 'Uploading...' : 'Click to upload photos'}</span>
              <p className="text-xs text-muted-foreground mt-1">Upload high quality images (max 10MB each)</p>
            </div>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={uploading} />
          </label>
          {images.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative group cursor-pointer" onClick={() => setPrimaryImage(i)}>
                  <img src={img.url} alt="" className="h-24 w-full object-cover rounded-lg" />
                  {img.is_primary && <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">Cover</span>}
                  <button onClick={e => { e.stopPropagation(); removeImage(i); }} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Contact Person</Label><Input value={contactPerson} onChange={e => setContactPerson(e.target.value)} placeholder="Your name" /></div>
          <div><Label>Phone</Label><Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+251..." /></div>
          <div><Label>Email</Label><Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="email@example.com" /></div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end pb-8">
        <Button variant="outline" onClick={() => saveMutation.mutate(false)} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-1" />Save as Draft
        </Button>
        <Button onClick={() => saveMutation.mutate(true)} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <SendHorizonal className="h-4 w-4 mr-1" />}
          Submit for Review
        </Button>
      </div>
    </div>
  );
}
